/* tslint:disable:max-line-length */
import WidgetModel, {
  DialogueWidget,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import { Avatar, Link } from '@mm/components/src';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import './WidgetPreview.pcss';
import moment, { duration } from 'moment';
import ButtonModel, {
  ButtonType,
  OptInButtonConfig,
} from '@mm/state/model/ButtonModel';
import { BUTTON_NAME } from '../../common/widgets/OtnWidget/OtnWidget';
import {
  ButtonColor,
  PrimaryButton,
} from '../../common/buttons/PrimaryButton/PrimaryButton';

const LINK_REGEX = /(https?:\/\/[^\s]+)/g;

type Props = {
  className?: string;
  platforms?: PlatformType[];
  avatarSrc: string;
  widget: WidgetModel;
  setActiveWidgetIndex: () => void;
  setPreviewToStore: (w: WidgetModel, btnType?: ButtonType) => void;
  currentDialogueId?: number;
  isSms?: boolean;
};

const getAudioElBySrc = src =>
  Array.from(document.querySelectorAll('audio')).filter(
    el => el.src === src,
  )[0];

const AudioWidgetPreview = ({ widget, avatarSrc }: Partial<Props>) => {
  const [audioDuration, setAudioDuration] = useState('');
  const [play, setPlay] = useState(false);

  const ref = useRef<HTMLAudioElement>(null);

  const handlePlay = () => {
    if (
      ref.current &&
      !ref.current.paused &&
      !ref.current.ended &&
      ref.current.currentTime > 0 &&
      ref?.current.readyState > 2
    ) {
      ref?.current?.pause();
      setPlay(false);
      return;
    }

    ref?.current?.play();
    setPlay(true);
  };

  const up = v =>
    setAudioDuration(
      moment.utc(duration(v, 'seconds').as('milliseconds')).format('m:ss'),
    );

  const listener = e => up(e.target?.duration);

  const loadedmetadata = e => up(e.target?.duration);

  useEffect(() => {
    if (ref?.current?.duration) {
      return up(ref.current?.duration);
    }

    if (ref?.current && !audioDuration) {
      ref?.current?.addEventListener('durationchange', listener);
      ref?.current?.addEventListener('loadedmetadata', loadedmetadata);
    }

    return () => {
      if (ref?.current) {
        ref.current?.removeEventListener('loadedmetadata', listener);
        ref.current?.removeEventListener('durationchange', listener);
      }
    };
  }, []);

  return (
    <div className="wp-widget">
      <Avatar className="dp-avatar" src={avatarSrc} />
      <div className="wp-message">
        <div className="wp-audio">
          <audio src={widget?.file} ref={ref} />

          <span className="wp-audio-play" onClick={handlePlay}>
            {play ? (
              <svg
                className="crt8y2ji"
                width="24px"
                height="24px"
                viewBox="0 0 36 36"
              >
                <path
                  d="M11 8.5A1.5 1.5 0 009.5 10v16a1.5 1.5 0 001.5 1.5h4a1.5 1.5 0 001.5-1.5V10A1.5 1.5 0 0015 8.5h-4zm10 0a1.5 1.5 0 00-1.5 1.5v16a1.5 1.5 0 001.5 1.5h4a1.5 1.5 0 001.5-1.5V10A1.5 1.5 0 0025 8.5h-4z"
                  fill="black"
                  fillRule="evenodd"
                  stroke="none"
                />
              </svg>
            ) : (
              <svg width="24px" height="24px" viewBox="0 0 36 36">
                <path
                  d="M10 25.498V10.502a1.5 1.5 0 012.17-1.342l14.997 7.498c1.105.553 1.105 2.13 0 2.684L12.17 26.84A1.5 1.5 0 0110 25.498z"
                  fill="black"
                  fillRule="evenodd"
                  stroke="none"
                />
              </svg>
            )}
          </span>
          <span className="wp-audio-seconds">{audioDuration || '0:00'}</span>
        </div>
      </div>
    </div>
  );
};

// tslint:disable-next-line:cyclomatic-complexity
export const WidgetPreview = ({
  className,
  widget,
  platforms,
  avatarSrc,
  setActiveWidgetIndex,
  setPreviewToStore,
  currentDialogueId,
  isSms,
}: Props) => {
  const { config } = widget;
  let { buttons } = widget;
  let { body: message } = config;
  const exceptionInstagramButtonTypes = [
    ButtonType.CALL,
    ButtonType.PAGE,
    ButtonType.SHARE,
    ButtonType.POSTBACK,
    ButtonType.EMAIL,
    ButtonType.PHONE_NUMBER,
    ButtonType.LOCATION,
    ButtonType.HANDOVER,
    ButtonType.OPT_IN,
  ];
  const pages = useSelector((state: StoreState) => state.page.entities);

  buttons =
    platforms?.includes('instagram') && widget.type === WidgetTypes.TEXT
      ? buttons.filter(b => !exceptionInstagramButtonTypes.includes(b.type))
      : buttons.filter(
          b =>
            b.type !== ButtonType.EMAIL && b.type !== ButtonType.PHONE_NUMBER,
        );

  const getSelectedBtnIdx = (idx: number, btnType) => {
    if ([ButtonType.PAGE, ButtonType.OPT_IN].includes(btnType)) {
      return widget.metadata?.selectedBtnIdx !== idx ? idx : undefined;
    }
    return idx;
  };

  const setPathForTextWidgets = async (idx: number, btn: ButtonModel) => {
    if (
      ![ButtonType.CALL, ButtonType.URL, ButtonType.HANDOVER].includes(btn.type)
    ) {
      setActiveWidgetIndex();
      const selectedBtnIdx =
        btn.type === ButtonType.OPT_IN || btn.type === ButtonType.PAGE
          ? (btn.config as OptInButtonConfig).pageId
            ? widget.metadata?.selectedBtnIdx !== idx
              ? idx
              : undefined
            : idx
          : idx;
      const btnType =
        btn.type === ButtonType.OPT_IN
          ? (btn.config as OptInButtonConfig).pageId &&
            selectedBtnIdx !== undefined
            ? btn.type
            : undefined
          : selectedBtnIdx !== undefined
          ? btn.type
          : undefined;
      setPreviewToStore(
        {
          ...widget,
          metadata: {
            ...widget.metadata,
            selectedBtnIdx,
          },
        },
        btnType,
      );
    }
  };

  const setPathForGalleryWidget = async (
    idx: number,
    btnIdx: number,
    btnType?: ButtonType,
  ) => {
    if (btnType && ![ButtonType.CALL, ButtonType.URL].includes(btnType)) {
      setActiveWidgetIndex();
      const selectedBtnIdx = getSelectedBtnIdx(btnIdx, btnType);
      const selectedEntryIdx =
        widget.metadata?.selectedEntryIdx !== idx ||
        selectedBtnIdx !== undefined
          ? idx
          : undefined;
      setPreviewToStore(
        {
          ...widget,
          metadata: {
            ...widget.metadata,
            selectedBtnIdx,
            selectedEntryIdx,
          },
        },
        selectedBtnIdx !== undefined ? btnType : undefined,
      );
    }
  };

  const setPathForFormWidget = async (
    entryId: number,
    btnIdx: number,
    btnType: ButtonType,
  ) => {
    const idx =
      widget.config.entries?.findIndex(entry => entry.id === entryId) ?? 0;
    const selectedBtnForEntry = [
      ...(widget.metadata?.selectedBtnForEntry ??
        Array(widget.config.entries?.length).fill(undefined)),
    ];
    selectedBtnForEntry[idx] = btnIdx;

    setActiveWidgetIndex();
    setPreviewToStore(
      {
        ...widget,
        metadata: {
          ...widget.metadata,
          selectedBtnForEntry,
        },
      },
      btnType,
    );
  };

  const [galleryIdx, setGalleryIdx] = useState(0);
  let messageElement;

  switch (widget.type) {
    case WidgetTypes.TEXT:
    case WidgetTypes.SMS_OPT_IN_REPLY:
    case WidgetTypes.OTN:
    case WidgetTypes.OPT_IN:
      if (widget.type === WidgetTypes.OTN) {
        message = config?.title;
        buttons = [
          {
            name: BUTTON_NAME,
            type: ButtonType.OPT_IN,
            config: { pageId: widget.config.pageId },
          } as ButtonModel,
        ];
      }
      if (widget.type === WidgetTypes.OPT_IN && message) {
        const url = message.match(LINK_REGEX);
        if (url?.[0]) {
          message = message.replace(LINK_REGEX, '');
          messageElement = (
            <>
              {message}{' '}
              <Link href={url[0]} isBlank className="wp-message-link">
                {url}
              </Link>
            </>
          );
        }
      }
      if (
        isSms &&
        (widget.type === WidgetTypes.OPT_IN || widget.type === WidgetTypes.TEXT)
      ) {
        buttons = [];
      }
      if (!message) {
        return <></>;
      }

      return (
        <div className="wp-widget">
          <Avatar className="dp-avatar" src={avatarSrc} />

          <div className="wp-text">
            <div
              className={classNames('wp-message', {
                'wp-message--header': buttons?.length,
              })}
            >
              {messageElement || message}
            </div>

            {!!buttons?.length && (
              <div className="wp-text__buttons">
                {buttons.map((button, idx) => (
                  <div
                    key={button.id}
                    className="wp-text__button"
                    onClick={() => setPathForTextWidgets(idx, button)}
                  >
                    {button.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );

    case WidgetTypes.GALLERY:
      if (!widget.config.entries?.length) {
        return <></>;
      }
      return (
        <div className="wp-widget">
          <Avatar className="dp-avatar" src={avatarSrc} />

          <div className="wp-gallery">
            {widget.config?.entries?.map?.((entry, idx) => (
              <div
                className={classNames('wp-gallery__entry', {
                  'wp-gallery__entry--visible': idx === galleryIdx,
                })}
                key={entry.id}
              >
                <div className="wp-gallery__header">
                  <img src={entry.file} alt="" />
                </div>

                {(entry.title || entry.description) && (
                  <div className="wp-gallery__text">
                    {entry.title && (
                      <span className="wp-gallery__title">{entry.title}</span>
                    )}

                    {entry.description && (
                      <span className="wp-gallery__subtitle">
                        {entry.description}
                      </span>
                    )}
                  </div>
                )}

                {!!entry.buttons?.length && (
                  <div className="wp-text__buttons">
                    {entry.buttons.map((button, btnIdx) => (
                      <div
                        key={button.id}
                        className="wp-text__button"
                        onClick={() =>
                          setPathForGalleryWidget(idx, btnIdx, button.type)
                        }
                      >
                        {button.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {galleryIdx > 0 && widget?.config?.entries?.length > 1 && (
              <div
                className="wp-gallery__prev"
                onClick={() => setGalleryIdx(galleryIdx - 1)}
              >
                <svg viewBox="0 0 20 20" width="1em" height="1em">
                  <path d="M7.8 4.53 13.273 10 7.8 15.47a.75.75 0 0 0 1.061 1.06l6-6a.751.751 0 0 0 0-1.06l-6-6A.75.75 0 0 0 7.8 4.53z" />
                </svg>
              </div>
            )}

            {galleryIdx + 1 < widget?.config?.entries?.length && (
              <div
                className="wp-gallery__next"
                onClick={() => setGalleryIdx(galleryIdx + 1)}
              >
                <svg viewBox="0 0 20 20" width="1em" height="1em">
                  <path d="M7.8 4.53 13.273 10 7.8 15.47a.75.75 0 0 0 1.061 1.06l6-6a.751.751 0 0 0 0-1.06l-6-6A.75.75 0 0 0 7.8 4.53z" />
                </svg>
              </div>
            )}
          </div>
        </div>
      );

    case WidgetTypes.QUICK_QUESTION:
    case WidgetTypes.SMS_OPT_IN:
    case WidgetTypes.SMS_OPT_IN_V2:
      if (!message) {
        return <></>;
      }

      return (
        <div className="wp-widget">
          <Avatar className="dp-avatar" src={avatarSrc} />
          <div className="wp-message">{message}</div>

          {!!buttons.length && (
            <>
              <div className="wp-qq__buttons">
                {buttons.map((button, idx) => (
                  <div
                    key={button.id}
                    className="wp-qq__button"
                    onClick={() => setPathForTextWidgets(idx, button)}
                  >
                    {button.config?.['file'] && (
                      <Avatar
                        src={button.config?.['file']}
                        className="wp-qq__button-img"
                      />
                    )}
                    {button.name}
                  </div>
                ))}
              </div>
              {isSms && (
                <div className="wp__buttons-sms-warning">
                  The answer buttons are only visible to you in the preview
                </div>
              )}
            </>
          )}
        </div>
      );

    case WidgetTypes.FORM:
      return (
        <>
          {widget?.metadata?.formWidgets?.map((formWidget: DialogueWidget) => {
            if (formWidget.type === 'user_message') {
              return (
                <div className="dp-user-message-container" key={formWidget.id}>
                  <div className="dp-user-message">{formWidget.message}</div>
                </div>
              );
            }
            const hasBtns = formWidget?.buttons?.some(btn => btn.name);
            return (
              <div className="wp-widget" key={formWidget.id}>
                <Avatar className="dp-avatar" src={avatarSrc} />
                <div className="wp-message">{formWidget.config.body}</div>

                {!!formWidget?.buttons?.length && (
                  <>
                    <div className="wp-qq__buttons">
                      {formWidget.buttons.map((button, btnIdx) => {
                        if (button.name) {
                          return (
                            <div
                              key={button.id}
                              className="wp-qq__button"
                              onClick={() =>
                                setPathForFormWidget(
                                  formWidget.id,
                                  btnIdx,
                                  button.type,
                                )
                              }
                            >
                              {button.name}
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                    {isSms && hasBtns && (
                      <div className="wp__buttons-sms-warning">
                        The answer buttons are only visible to you in the
                        preview
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </>
      );

    case WidgetTypes.ATTACHMENT: {
      if (!widget.file) {
        return <></>;
      }

      return (
        <div className="wp-widget">
          <Avatar className="dp-avatar" src={avatarSrc} />
          <div className="wp-message">
            <div className="wp-attachment">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="24"
                viewBox="0 0 26 24"
              >
                <title>file</title>
                <g fill="none" fillRule="evenodd">
                  <path d="M18.667 24H0V0h10.667v8h8v16z" fill="#dedede" />
                  <path d="M12 0l6.667 6.667H12V0z" fill="#ACACAC" />
                </g>
              </svg>

              <span>{widget.config.title}</span>
            </div>
          </div>
        </div>
      );
    }

    case WidgetTypes.NAVIGATE: {
      const button = {
        name: 'NAVIGATE',
        type: ButtonType.PAGE,
        config: { pageId: widget.config.pageId },
      } as ButtonModel;

      const pathPage = pages.find(item => item.id === widget.config.pageId);
      const dialogueName = pathPage ? pathPage.name : 'another';

      return (
        <>
          <div className="wp-navigate">
            <div className="wp-navigate__info">
              Seen only by you:
              <br />
              {currentDialogueId !== config.pageId &&
              config.pageId !== widget.pageId ? (
                <span className="wp-navigate__dialogue-description">
                  Navigating to {dialogueName} dialogue
                </span>
              ) : (
                <span className="wp-navigate__same-dialogue-description">
                  Same dialog will be triggered
                </span>
              )}
            </div>
            <PrimaryButton
              color={ButtonColor.transparent}
              className="wp-navigate__button"
              onClick={() => {
                setPathForTextWidgets(0, button);
              }}
              disabled={
                config.pageId === widget.pageId ||
                currentDialogueId === config.pageId
              }
            >
              {widget.metadata?.selectedBtnIdx === undefined
                ? 'Show Preview'
                : 'Hide Preview'}
            </PrimaryButton>
          </div>
          {(widget.metadata?.isPathEmpty ||
            widget.metadata?.noSupportedWidgets) &&
            widget.metadata?.selectedBtnIdx !== undefined && (
              <div className="wp-navigate__empty-dialogue">
                {widget.metadata?.noSupportedWidgets
                  ? 'No supported widgets'
                  : 'Dialogue is empty'}
              </div>
            )}
        </>
      );
    }
    case WidgetTypes.AUDIO:
      return <AudioWidgetPreview widget={widget} avatarSrc={avatarSrc} />;

    case WidgetTypes.IMAGE:
    case WidgetTypes.GIF:
      return (
        <div className="wp-widget">
          <Avatar className="dp-avatar" src={avatarSrc} />
          <div className="wp-message">
            <div className="wp-picture">
              <img src={widget.file} alt="image" />
            </div>
          </div>
        </div>
      );

    case WidgetTypes.VIDEO:
      return (
        <div className="wp-widget">
          <Avatar className="dp-avatar" src={avatarSrc} />
          <div className="wp-message">
            <div className="wp-video">
              <video controls src={widget.file} />
            </div>
          </div>
        </div>
      );

    default:
      return <></>;
  }
};
